// @flow
import React, { useState, useEffect, useRef } from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { pad } from '../../../utils';
import { toDurationObject, fromDurationObject } from '../../../utils/zoneUtils';
import PartialInput, { selectAll } from './PartialInput';
import makeStyles from "@material-ui/core/styles/makeStyles";

const useStyles = makeStyles({
    root: {
        whiteSpace: 'nowrap',
        fontFamily: '"Roboto Mono", monospace',
        letterSpacing: '-0.5px'
    },
});

const padObject = (object) => {
    const paddedObject = {};
    for(const key in object) {
        if(Object.prototype.hasOwnProperty.call(object, key)) {
            paddedObject[key] = pad(object[key]);
        }
    }

    return paddedObject;
};

const emptyDurationObject = padObject(toDurationObject(0));

type Props = $ReadOnly<{
    duration: ?number,
    disabled?: boolean,

    onChange: (value: number) => void
}>;

function DurationInput({ duration, disabled, onChange }: Props) {
    const classes = useStyles();

    const focusRef = useRef(new Set());
    const [touched, setTouched] = useState(false);
    const [editedDuration, setEditedDuration] = useState();
    const [durationObject, setDurationObject] = useState(duration && toDurationObject(duration));

    const handleChange = () => {
        if(touched) {
            setDurationObject(editedDuration);
            onChange(fromDurationObject(editedDuration));
        }

        setTouched(false);
        setEditedDuration(null);
    };

    const handleFocus = (e) => {
        e.persist();
        focusRef.current.add(e.target);

        if(!editedDuration) {
            setEditedDuration({
                ...(durationObject ? padObject(durationObject) : emptyDurationObject)
            });

            // Select input again after default value was set
            setTimeout(() => setTimeout(() => selectAll(e.target)));
        }
    };

    const handleBlur = (e) => {
        focusRef.current.delete(e.target);

        setTimeout(() => {
            if(focusRef.current.size === 0) {
                handleChange();
            }
        });
    };

    const handlePartialChange = (type) => (value) => {
        setTouched(true);
        setEditedDuration({
            ...editedDuration,
            [type]: value
        });
    };

    const handleEnter = (e) => {
        e.target.blur();
    };

    const handleClickAway = () => {
        focusRef.current.forEach((input) => {
            if(input === document.activeElement) {
                input.blur()
            }
        });
    };

    useEffect(() => {
        setDurationObject(duration && toDurationObject(duration));
    }, [duration]);

    const getValue = (type) => ( editedDuration ? editedDuration[type] : (durationObject ? pad(durationObject[type]) : '--') );

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div className={classes.root}>
                <PartialInput
                    value={getValue('hours')}
                    disabled={disabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handlePartialChange('hours')}
                    onEnter={handleEnter}
                />
                :
                <PartialInput
                    value={getValue('minutes')}
                    max={60}
                    disabled={disabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handlePartialChange('minutes')}
                    onEnter={handleEnter}
                />
                :
                <PartialInput
                    value={getValue('seconds')}
                    max={60}
                    disabled={disabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handlePartialChange('seconds')}
                    onEnter={handleEnter}
                />
            </div>
        </ClickAwayListener>
    );
}

export default DurationInput;